import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Video, Upload, Loader2, Download, PlayCircle, Mic2, Clock, Sparkles, ExternalLink, Wand2, TextCursorInput, Newspaper, Image as ImageIcon, Film, Zap } from "lucide-react";

const VOICES = [
  { id: "onyx", label: "Onyx — गंभीर आवाज़ (Chief Editor style)", tag: "🎙️ Boss" },
  { id: "echo", label: "Echo — शांत आवाज़", tag: "🧘" },
  { id: "nova", label: "Nova — ऊर्जावान महिला आवाज़", tag: "🔥" },
  { id: "shimmer", label: "Shimmer — प्रसन्न महिला आवाज़", tag: "✨" },
  { id: "sage", label: "Sage — बुद्धिमान", tag: "🦉" },
];
const DURATIONS = [30, 60, 90];

function fileHost() { return API.replace(/\/api$/, ""); }

export default function VideoEditorAdminPage() {
  const [file, setFile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [mode, setMode] = useState("video"); // "video" | "photo"
  const [smartCaptions, setSmartCaptions] = useState(true);
  const [text, setText] = useState("");
  const [ticker, setTicker] = useState("");
  const [voice, setVoice] = useState("onyx");
  const [duration, setDuration] = useState(60);
  const [generating, setGenerating] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [aiScripting, setAiScripting] = useState(false);
  const [oneClickBusy, setOneClickBusy] = useState(false);
  const [oneClickStage, setOneClickStage] = useState(""); // 'script' | 'video' | 'publish'
  const [publishing, setPublishing] = useState(false);
  const [publishedSlug, setPublishedSlug] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const fileRef = useRef();

  const load = () => api.get("/admin/video-editor/history").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("video/")) return toast.error("सिर्फ़ video file अपलोड करें");
    if (f.size > 100 * 1024 * 1024) return toast.error("Video 100 MB से बड़ी है");
    setFile(f);
  };

  const pickPhotos = (files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    if (arr.length > 12) return toast.error("Max 12 photos");
    for (const f of arr) {
      if (!f.type.startsWith("image/")) return toast.error("सिर्फ़ image files");
      if (f.size > 10 * 1024 * 1024) return toast.error(`${f.name} 10 MB से बड़ी है`);
    }
    setPhotos(arr);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "video" && !file) return toast.error("पहले video select करें");
    if (mode === "photo" && photos.length === 0) return toast.error("कम से कम 1 photo select करें");
    if (!text.trim()) return toast.error("Hindi text लिखें");
    setGenerating(true); setResult(null);
    try {
      const fd = new FormData();
      if (mode === "video") fd.append("file", file);
      else photos.forEach((p) => fd.append("files", p));
      fd.append("hindi_text", text);
      fd.append("voice", voice);
      fd.append("duration", String(duration));
      if (ticker.trim()) fd.append("ticker", ticker.trim());
      if (mode === "photo") fd.append("smart_captions", smartCaptions ? "true" : "false");
      const endpoint = mode === "video" ? "/admin/video-editor/generate" : "/admin/photo-video/generate";
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setResult(data);
      toast.success(`Video तैयार! ${data.size_mb} MB · ${data.duration}s`);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setGenerating(false); }
  };

  const rewrite = async () => {
    if (!text.trim()) return toast.error("पहले script में कुछ लिखें, फिर AI उसे professional कर देगा");
    setRewriting(true);
    try {
      const { data } = await api.post("/admin/video-editor/rewrite", { text });
      if (data?.rewritten) {
        setText(data.rewritten);
        toast.success("✨ Script polished!");
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setRewriting(false); }
  };

  const aiFullReportage = async () => {
    if (photos.length === 0) return toast.error("पहले photos select करें");
    setAiScripting(true);
    try {
      const fd = new FormData();
      photos.forEach((p) => fd.append("files", p));
      fd.append("duration", String(duration));
      if (text.trim()) fd.append("hint", text.trim().slice(0, 300));
      const { data } = await api.post("/admin/photo-video/ai-script", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      if (data?.script) {
        setText(data.script);
        if (data.ticker && !ticker.trim()) setTicker(data.ticker);
        toast.success(`✨ AI ने ${photos.length} photos से पूरी reportage बना दी!`);
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setAiScripting(false); }
  };

  const publishAsNews = async () => {
    if (!result?.job_id) return;
    setPublishing(true);
    try {
      const { data } = await api.post(`/admin/video-editor/${result.job_id}/publish-as-news`, {
        category: "वीडियो", is_breaking: false,
      });
      setPublishedSlug(data.slug);
      toast.success("📰 News के रूप में प्रकाशित!");
    } catch (e) { toast.error(formatError(e)); }
    finally { setPublishing(false); }
  };

  const oneClickPublish = async () => {
    if (photos.length === 0) return toast.error("पहले photos select करें");
    setOneClickBusy(true); setResult(null); setPublishedSlug(null);
    try {
      // 1) AI script from photos
      setOneClickStage("script");
      const sfd = new FormData();
      photos.forEach((p) => sfd.append("files", p));
      sfd.append("duration", String(duration));
      if (text.trim()) sfd.append("hint", text.trim().slice(0, 300));
      const { data: aiRes } = await api.post("/admin/photo-video/ai-script", sfd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      if (!aiRes?.script) throw new Error("AI script खाली मिली");
      setText(aiRes.script);
      const nextTicker = (ticker.trim() || aiRes.ticker || "").trim();
      if (nextTicker && !ticker.trim()) setTicker(nextTicker);
      toast.success("✨ AI script तैयार · अब video बन रही है…");

      // 2) Video generation
      setOneClickStage("video");
      const gfd = new FormData();
      photos.forEach((p) => gfd.append("files", p));
      gfd.append("hindi_text", aiRes.script);
      gfd.append("voice", voice);
      gfd.append("duration", String(duration));
      if (nextTicker) gfd.append("ticker", nextTicker);
      gfd.append("smart_captions", smartCaptions ? "true" : "false");
      const { data: vidRes } = await api.post("/admin/photo-video/generate", gfd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setResult(vidRes);
      toast.success(`🎥 Video ready · ${vidRes.size_mb} MB · अब portal पर publish हो रहा है…`);

      // 3) Publish as news
      setOneClickStage("publish");
      const { data: pubRes } = await api.post(`/admin/video-editor/${vidRes.job_id}/publish-as-news`, {
        title: aiRes.title || undefined,
        category: "वीडियो",
        is_breaking: false,
      });
      setPublishedSlug(pubRes.slug);
      toast.success("🎉 पूरी news portal पर live!");
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setOneClickBusy(false);
      setOneClickStage("");
    }
  };

  return (
    <div data-testid="video-editor-page">
      <div className="mb-6">
        <h1 className="font-devnagri-heading text-3xl font-black text-[#0F172A] flex items-center gap-3">
          <Video className="w-7 h-7 text-[#DC2626]"/> Video Editor
        </h1>
        <p className="text-sm text-slate-500 mt-1">कोई भी ग्राउंड वीडियो अपलोड करो + Hindi text लिखो → 60-सेकंड ब्रांडेड news video 2-3 मिनट में तैयार</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <form onSubmit={submit} className="xl:col-span-3 bg-white rounded-xl border-2 border-slate-200 p-6 space-y-5">
          {/* Mode toggle */}
          <div className="flex gap-2 p-1 bg-slate-100 rounded-lg" data-testid="ve-mode-tabs">
            <button type="button" onClick={() => setMode("video")}
              className={`flex-1 py-2.5 rounded-md text-sm font-bold transition inline-flex items-center justify-center gap-2 ${mode === "video" ? "bg-white shadow text-[#DC2626]" : "text-slate-600 hover:text-slate-800"}`}
              data-testid="ve-mode-video">
              <Film className="w-4 h-4"/> Video से
            </button>
            <button type="button" onClick={() => setMode("photo")}
              className={`flex-1 py-2.5 rounded-md text-sm font-bold transition inline-flex items-center justify-center gap-2 ${mode === "photo" ? "bg-white shadow text-[#DC2626]" : "text-slate-600 hover:text-slate-800"}`}
              data-testid="ve-mode-photo">
              <ImageIcon className="w-4 h-4"/> Photos से
            </button>
          </div>

          {/* Upload */}
          {mode === "video" ? (
          <div>
            <label className="text-[11px] uppercase tracking-widest font-black text-slate-500 mb-2 block">📹 Ground Video (max 100 MB)</label>
            <input ref={fileRef} type="file" accept="video/*" className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])} data-testid="ve-file"/>
            <div onClick={() => fileRef.current?.click()}
              className="cursor-pointer rounded-lg border-2 border-dashed border-slate-300 hover:border-[#EA580C] p-6 bg-slate-50 hover:bg-orange-50 transition">
              {file ? (
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-md bg-[#DC2626] text-white flex items-center justify-center">
                    <PlayCircle className="w-8 h-8"/>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm text-[#0F172A] truncate">{file.name}</div>
                    <div className="text-xs text-slate-500">{(file.size / (1024*1024)).toFixed(1)} MB</div>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setFile(null); if (fileRef.current) fileRef.current.value = ""; }}>बदलें</Button>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2"/>
                  <div className="font-bold text-slate-700">Video select करने के लिए click करें</div>
                  <div className="text-xs text-slate-500 mt-1">MP4, MOV, AVI · WhatsApp forwards भी काम करेंगे</div>
                </div>
              )}
            </div>
          </div>
          ) : (
          <div>
            <label className="text-[11px] uppercase tracking-widest font-black text-slate-500 mb-2 block">📸 Photos (1-12 · Ken Burns zoom slideshow banega)</label>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
              onChange={(e) => pickPhotos(e.target.files)} data-testid="ve-photos"/>
            <div onClick={() => fileRef.current?.click()}
              className="cursor-pointer rounded-lg border-2 border-dashed border-slate-300 hover:border-[#EA580C] p-6 bg-slate-50 hover:bg-orange-50 transition">              {photos.length > 0 ? (
                <div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-3">
                    {photos.map((p, i) => (
                      <div key={i} className="aspect-square rounded overflow-hidden border border-slate-200">
                        <img src={URL.createObjectURL(p)} alt="" className="w-full h-full object-cover"/>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-600 font-bold">{photos.length} photo{photos.length > 1 ? "s" : ""} selected</span>
                    <Button type="button" variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setPhotos([]); if (fileRef.current) fileRef.current.value = ""; }}>Clear</Button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <ImageIcon className="w-10 h-10 text-slate-400 mx-auto mb-2"/>
                  <div className="font-bold text-slate-700">Photos select करने के लिए click करें</div>
                  <div className="text-xs text-slate-500 mt-1">1-12 photos · JPG/PNG · अधिकतम 10 MB per photo</div>
                </div>
              )}
            </div>
            {/* Smart AI captions toggle */}
            <label className="mt-3 flex items-start gap-3 p-3 rounded-lg border-2 border-violet-200 bg-violet-50 hover:bg-violet-100 transition cursor-pointer" data-testid="ve-smart-cap-toggle">
              <input type="checkbox" checked={smartCaptions} onChange={(e) => setSmartCaptions(e.target.checked)} className="mt-1 w-4 h-4 accent-violet-600" data-testid="ve-smart-cap-checkbox"/>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 font-black text-sm text-violet-900">
                  <Sparkles className="w-3.5 h-3.5"/> AI Smart Captions (Gemini Vision)
                  <span className="text-[9px] font-bold bg-violet-600 text-white px-1.5 py-0.5 rounded ml-1">NEW</span>
                </div>
                <div className="text-[11px] text-violet-700 mt-0.5">हर photo को AI देखकर उसके हिसाब से context-aware Hindi caption लिखेगा · Off करने पर script के sentences use होंगे</div>
              </div>
            </label>
            {/* AI Full Reportage button */}
            <Button
              type="button"
              onClick={aiFullReportage}
              disabled={aiScripting || oneClickBusy || photos.length === 0}
              className="mt-3 w-full bg-gradient-to-r from-fuchsia-600 via-violet-600 to-indigo-600 hover:from-fuchsia-700 hover:to-indigo-700 text-white font-black text-sm py-4 shadow-lg"
              data-testid="ve-ai-fullscript"
            >
              {aiScripting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>AI Photos देख रहा है… (30-60 sec)</>
              ) : (
                <><Wand2 className="w-4 h-4 mr-2"/>🪄 सिर्फ़ Photos से पूरी Reportage बनाओ (Title + Script + Ticker)</>
              )}
            </Button>
            <p className="text-[11px] text-fuchsia-700 mt-1.5 text-center">
              Gemini Vision आपकी सभी photos देखकर headline, {duration}-sec Hindi script और breaking-news ticker automatic बना देगा
            </p>

            {/* One-Click Generate + Publish (chain of 3 endpoints) */}
            <div className="mt-4 rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 p-3">
              <Button
                type="button"
                onClick={oneClickPublish}
                disabled={oneClickBusy || aiScripting || generating || photos.length === 0}
                className="w-full bg-gradient-to-r from-amber-500 via-orange-600 to-rose-600 hover:from-amber-600 hover:to-rose-700 text-white font-black text-base py-6 shadow-xl"
                data-testid="ve-one-click-publish"
              >
                {oneClickBusy ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin"/>
                    {oneClickStage === "script" && "1/3 · AI Script बना रहा है…"}
                    {oneClickStage === "video" && "2/3 · Video Render हो रहा है…"}
                    {oneClickStage === "publish" && "3/3 · Portal पर Publish हो रहा है…"}
                  </>
                ) : (
                  <><Zap className="w-5 h-5 mr-2"/>⚡ Photos से सीधा Live News बनाओ (एक Click में सब कुछ)</>
                )}
              </Button>
              <div className="mt-2 grid grid-cols-3 gap-1 text-[10px] font-bold text-center">
                <div className={`py-1 rounded ${oneClickBusy && oneClickStage === "script" ? "bg-amber-600 text-white animate-pulse" : oneClickBusy || publishedSlug ? "bg-emerald-500 text-white" : "bg-white/60 text-amber-800"}`}>1 · AI Script</div>
                <div className={`py-1 rounded ${oneClickBusy && oneClickStage === "video" ? "bg-orange-600 text-white animate-pulse" : (oneClickStage === "publish" || publishedSlug) ? "bg-emerald-500 text-white" : "bg-white/60 text-amber-800"}`}>2 · Video</div>
                <div className={`py-1 rounded ${oneClickBusy && oneClickStage === "publish" ? "bg-rose-600 text-white animate-pulse" : publishedSlug ? "bg-emerald-500 text-white" : "bg-white/60 text-amber-800"}`}>3 · Publish</div>
              </div>
              <p className="text-[11px] text-amber-800 mt-2 text-center">
                Photos upload → AI Hindi script → Boss voice-over → Ken Burns slideshow → 3-sec intro sting → Live news article — 90 seconds में सब।
              </p>
            </div>
          </div>
          )}

          {/* Hindi text */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] uppercase tracking-widest font-black text-slate-500 flex items-center gap-1">
                <Mic2 className="w-3 h-3"/> Hindi Script (जो voice बोलेगी)
              </label>
              <Button type="button" onClick={rewrite} disabled={rewriting || !text.trim()} size="sm" variant="outline" className="h-7 text-xs border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="ve-rewrite">
                {rewriting ? <Loader2 className="w-3 h-3 mr-1 animate-spin"/> : <Wand2 className="w-3 h-3 mr-1"/>}
                AI से बेहतर करें
              </Button>
            </div>
            <Textarea rows={7} value={text} onChange={(e) => setText(e.target.value)} maxLength={1500}
              placeholder="जैसे: यह वीडियो ग्राम पंचायत रामकोला के पंडोपारा स्कूल का है। इस स्कूल की छत इतनी जर्जर हो चुकी है कि..."
              data-testid="ve-text"/>
            <div className="text-[10px] text-slate-400 mt-1 text-right">{text.length}/1500</div>
          </div>

          {/* News Ticker (bottom scrolling bar) */}
          <div>
            <label className="text-[11px] uppercase tracking-widest font-black text-slate-500 mb-2 block flex items-center gap-1">
              <TextCursorInput className="w-3 h-3"/> Bottom News Ticker (slide करता हुआ · वैकल्पिक)
            </label>
            <input
              type="text"
              value={ticker}
              onChange={(e) => setTicker(e.target.value)}
              maxLength={200}
              placeholder="जैसे: छत्तीसगढ़ के सूरजपुर से ताज़ा खबर · ग्राम पंचायत रामकोला में स्कूल जर्जर हालत में"
              className="w-full text-sm rounded border-2 border-slate-200 focus:border-[#DC2626] p-2.5 focus:outline-none"
              data-testid="ve-ticker"/>
            <div className="text-[10px] text-slate-400 mt-1 flex justify-between">
              <span>💡 Video ke bottom pe red bar me right-to-left scroll hoga</span>
              <span>{ticker.length}/200</span>
            </div>
          </div>

          {/* Voice */}
          <div>
            <label className="text-[11px] uppercase tracking-widest font-black text-slate-500 mb-2 block">🎙️ Voice Style</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {VOICES.map((v) => (
                <label key={v.id} className={`cursor-pointer rounded-md border-2 p-2.5 flex items-center gap-2 transition ${voice === v.id ? "border-[#DC2626] bg-red-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`ve-voice-${v.id}`}>
                  <input type="radio" name="voice" value={v.id} checked={voice === v.id} onChange={(e) => setVoice(e.target.value)} className="accent-[#DC2626]"/>
                  <div className="text-lg">{v.tag}</div>
                  <div className="text-xs font-bold text-[#0F172A]">{v.label}</div>
                </label>
              ))}
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="text-[11px] uppercase tracking-widest font-black text-slate-500 mb-2 block flex items-center gap-1">
              <Clock className="w-3 h-3"/> Duration
            </label>
            <div className="flex gap-2">
              {DURATIONS.map((d) => (
                <button key={d} type="button" onClick={() => setDuration(d)}
                  className={`flex-1 py-2.5 rounded-md text-sm font-bold transition ${duration === d ? "bg-[#DC2626] text-white shadow" : "bg-white border-2 border-slate-200 text-slate-600 hover:border-[#DC2626]"}`}
                  data-testid={`ve-dur-${d}`}>{d} sec</button>
              ))}
            </div>
          </div>

          <Button type="submit" disabled={generating} className="w-full bg-gradient-to-r from-[#DC2626] to-[#EA580C] hover:from-[#B91C1C] hover:to-[#DC2626] text-white font-black text-base py-6" data-testid="ve-submit">
            {generating ? <><Loader2 className="w-5 h-5 mr-2 animate-spin"/>Video बन रहा है… (2-3 मिनट)</> : <><Sparkles className="w-5 h-5 mr-2"/>Branded Video बनाओ</>}
          </Button>
        </form>

        {/* Result + History */}
        <div className="xl:col-span-2 space-y-5">
          {result && (
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl p-5 shadow-md" data-testid="ve-result">
              <div className="text-[10px] uppercase tracking-widest font-black text-emerald-700 mb-1">✅ तैयार</div>
              <h3 className="font-devnagri-heading text-xl font-black text-[#0F172A] mb-3">Video Generated</h3>
              <video src={`${fileHost()}${result.url}`} controls className="w-full rounded-md mb-3 bg-black"/>
              <div className="text-xs text-slate-600 space-y-1 mb-3">
                <div>📏 {result.size_mb} MB · {result.duration}s · 720p</div>
                <div>🎙️ Voice: <b>{result.voice}</b></div>
              </div>
              <a href={`${fileHost()}${result.url}`} download className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-4 py-2.5 rounded-md mb-2" data-testid="ve-download">
                <Download className="w-4 h-4"/> Download करो
              </a>
              {publishedSlug ? (
                <a href={`/news/${publishedSlug}`} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-bold text-sm px-4 py-2.5 rounded-md" data-testid="ve-view-news">
                  <Newspaper className="w-4 h-4"/> प्रकाशित News देखो
                </a>
              ) : (
                <Button type="button" onClick={publishAsNews} disabled={publishing} className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-bold text-sm" data-testid="ve-publish">
                  {publishing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Publish हो रहा है…</> : <><Newspaper className="w-4 h-4 mr-2"/>Portal पर News के रूप में प्रकाशित करें</>}
                </Button>
              )}
              <p className="text-[10px] text-slate-500 mt-2 text-center">Direct URL: <code className="text-[10px] break-all">{fileHost()}{result.url}</code></p>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-bold text-sm text-[#0F172A] mb-3">पिछले Videos ({history.length})</h3>
            {history.length === 0 ? (
              <div className="text-xs text-slate-500 py-6 text-center">अभी कोई video generate नहीं हुआ</div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {history.map((h) => (
                  <a key={h.job_id} href={`${fileHost()}${h.url}`} target="_blank" rel="noopener noreferrer" className="block p-2 rounded-md hover:bg-slate-50 border border-slate-100 group" data-testid={`ve-hist-${h.job_id}`}>
                    <div className="flex items-center gap-2">
                      <PlayCircle className="w-8 h-8 text-slate-400 group-hover:text-[#DC2626] shrink-0"/>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-bold text-[#0F172A] truncate">{h.hindi_text || h.job_id}</div>
                        <div className="text-[10px] text-slate-500">{h.duration}s · {h.size_mb} MB · {h.voice}</div>
                      </div>
                      <ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#DC2626]"/>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
